import { useState } from 'react';

import { DiscrepancyIndicator } from '../badges/DiscrepancyIndicator';

import type { DiscrepancyIndicatorReal, SessionNoteSaveState } from '../../hooks/use-coattendance';
import type { PanelQualityVM, ParallelConvVM } from '../../view-models';
import type { MonitoringMode } from '../badges/SilentMonitoringIndicator';
import type React from 'react';

export interface CoAttendantToolsCoachingMessageVM {
  id: string;
  /** 'supervisor' (você) ou 'attendant' — derivado de fromUserId===currentUserId. */
  from: 'supervisor' | 'attendant';
  atLabel: string;
  text: string;
}

export interface CoAttendantToolsPanelProps {
  attendantName: string;
  monitoringMode: MonitoringMode;

  // ── Nota privada (D6) ──
  noteText: string;
  onNoteChange: (value: string) => void;
  noteSaveState: SessionNoteSaveState;

  // ── Chat lateral de coaching (D5) ──
  coachingMessages: CoAttendantToolsCoachingMessageVM[];
  onSendCoaching: (text: string) => void;
  isSendingCoaching: boolean;
  /** Mensagem de erro a exibir num `role=alert` quando o último envio falhou. */
  coachingSendFailed: boolean;

  // ── Indicadores de qualidade (display completo) ──
  quality: PanelQualityVM | null;

  // ── Discrepância (D7) — só Regulated em bond adversarial ──
  discrepancy: DiscrepancyIndicatorReal | null;
  showDiscrepancy: boolean;

  // ── Conversas paralelas (D10) ──
  parallelConvs: ParallelConvVM[];
  onSwitchConversation: (conversationId: string) => void;

  // ── Ações de força (D8 — só em conversa H_c, T2-3) ──
  /** Mostra a seção de força. Só true em H_c + modo visível, gated pelas caps de força. */
  showForceSection?: boolean;
  /** Supervisor tem `force-takeover`. */
  canForceTakeover?: boolean;
  /** Supervisor tem `force-playbook-switch`. */
  canForcePlaybook?: boolean;
  onOpenForceTakeover?: () => void;
  onOpenForcePlaybook?: () => void;
}

/**
 * CoAttendantToolsPanel — painel direito da Tela 2 (§7.6). Convertido de
 * design-references/brief-3/shared/cards-3.jsx:693-853.
 *
 * Divergências honestas (spec §5), aplicadas vs. o mockup:
 *   - Resumo vivo: OMITIDO (sem endpoint que sirva o resumo por conversa ao
 *     supervisor — não inventar bullets).
 *   - Notas privadas: um único scratchpad ligado à session-note (auto-save),
 *     não o array do mockup (scaffolding).
 *   - Qualidade: sentiment enum→label/tom (sem `/10`), adherence/usage em %
 *     (sem breakdown ✓✎✕), responseTime avg + p50/p95.
 *   - Force-takeover: FORA desta onda (T2-3).
 */
export function CoAttendantToolsPanel({
  attendantName,
  monitoringMode,
  noteText,
  onNoteChange,
  noteSaveState,
  coachingMessages,
  onSendCoaching,
  isSendingCoaching,
  coachingSendFailed,
  quality,
  discrepancy,
  showDiscrepancy,
  parallelConvs,
  onSwitchConversation,
  showForceSection = false,
  canForceTakeover = false,
  canForcePlaybook = false,
  onOpenForceTakeover,
  onOpenForcePlaybook,
}: CoAttendantToolsPanelProps): React.JSX.Element {
  const isInvisible = monitoringMode === 'invisible-to-all';
  const [chatMsg, setChatMsg] = useState('');

  const sendChat = (): void => {
    const trimmed = chatMsg.trim();
    // Guard anti duplo-submit — bloqueia vazio e envio em andamento.
    if (trimmed.length === 0 || isSendingCoaching) return;
    onSendCoaching(trimmed);
    setChatMsg('');
  };

  const saveLabel =
    noteSaveState === 'saving'
      ? 'salvando…'
      : noteSaveState === 'saved'
        ? 'salvo'
        : noteSaveState === 'error'
          ? 'falha ao salvar'
          : '';

  return (
    <aside className="coatt-tools" aria-label="Ferramentas de acompanhamento">
      {/* Notas privadas (D6 — single scratchpad, auto-save) */}
      <section className="coatt-tools__section">
        <h3 className="coatt-tools__title">
          Notas privadas
          {saveLabel !== '' && (
            <span
              className="mono"
              style={{
                marginLeft: 6,
                fontWeight: 'normal',
                fontSize: 'var(--text-2xs)',
                color: noteSaveState === 'error' ? 'var(--danger-fg)' : 'var(--fg-3)',
              }}
              role={noteSaveState === 'error' ? 'alert' : undefined}
            >
              · {saveLabel}
            </span>
          )}
        </h3>
        <textarea
          className="coatt-tools__note-input"
          value={noteText}
          onChange={(e) => {
            onNoteChange(e.target.value);
          }}
          placeholder="Adicionar nota privada…"
          rows={3}
        />
      </section>

      {/* Chat lateral c/ atendente (D5) — desabilitado em invisible-to-all */}
      <section
        className={'coatt-tools__section' + (isInvisible ? ' coatt-tools__section--disabled' : '')}
      >
        <h3 className="coatt-tools__title">
          Chat lateral c/ atendente
          {isInvisible && (
            <span style={{ marginLeft: 6, fontWeight: 'normal', color: 'var(--fg-3)' }}>
              · indisponível em modo invisível
            </span>
          )}
        </h3>
        {!isInvisible && (
          <>
            <div className="coatt-tools__chat-list">
              {coachingMessages.map((m) => (
                <div key={m.id} className={'coatt-chat coatt-chat--' + m.from}>
                  <span className="mono" style={{ color: 'var(--fg-3)' }}>
                    {m.atLabel}
                  </span>
                  <span>{m.text}</span>
                </div>
              ))}
            </div>
            {coachingSendFailed && (
              <div
                className="banner banner--danger"
                role="alert"
                style={{ marginBottom: 'var(--space-2)' }}
              >
                <div className="banner__body">
                  <span className="banner__title">Não foi possível enviar a mensagem.</span>
                </div>
              </div>
            )}
            <div className="coatt-tools__chat-composer">
              <input
                value={chatMsg}
                onChange={(e) => {
                  setChatMsg(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') sendChat();
                }}
                placeholder={`Mensagem para ${attendantName}…`}
                aria-label="Mensagem de coaching"
              />
              <button
                className="btn-ghost-mini btn-ghost-mini--primary"
                type="button"
                onClick={sendChat}
                disabled={isSendingCoaching || chatMsg.trim().length === 0}
              >
                Enviar
              </button>
            </div>
          </>
        )}
      </section>

      {/* Indicadores de qualidade (display completo, honesto) */}
      {quality !== null && (
        <section className="coatt-tools__section">
          <h3 className="coatt-tools__title">Indicadores de qualidade</h3>
          <div className="coatt-quality">
            <div className="coatt-quality__row">
              <span className="coatt-quality__label">Sentimento</span>
              <span className={`sla sla--${qualityToneToSla(quality.sentimentTone)}`}>
                {quality.sentimentLabel}
              </span>
            </div>
            <div className="coatt-quality__row">
              <span className="coatt-quality__label">Aderência ao playbook</span>
              <span>
                <b>{quality.adherencePct}%</b>
              </span>
            </div>
            <div className="coatt-quality__row">
              <span className="coatt-quality__label">Uso de sugestão IA</span>
              <span>
                <b>{quality.aiUsagePct}%</b>
              </span>
            </div>
            <div className="coatt-quality__row">
              <span className="coatt-quality__label">Tempo médio de resposta</span>
              <span>
                <b>{quality.avgResponseLabel}</b>
              </span>
            </div>
            {quality.sampleSize > 0 && (
              <div className="coatt-quality__row coatt-quality__row--sub">
                <span style={{ color: 'var(--fg-3)' }}>p50 · p95</span>
                <span style={{ color: 'var(--fg-3)' }}>
                  {quality.p50Label} · {quality.p95Label}
                </span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* DiscrepancyIndicator (D7 — Regulated em Bond adversarial) */}
      {showDiscrepancy && discrepancy !== null && (
        <section className="coatt-tools__section">
          <DiscrepancyIndicator
            attendantSees={discrepancy.attendantVisibleCount}
            supervisorSees={discrepancy.supervisorVisibleCount}
            discrepancyCount={discrepancy.discrepancyCount}
          />
        </section>
      )}

      {/* Ações de força (D8 — só em conversa H_c, modo visível, gated) */}
      {showForceSection && (canForceTakeover || canForcePlaybook) && (
        <section className="coatt-tools__section">
          <h3 className="coatt-tools__title">Ações de força (IA conduzindo)</h3>
          <p style={{ color: 'var(--fg-3)', fontSize: 'var(--text-xs)', marginTop: 0 }}>
            A IA está conduzindo a conversa autonomamente. Você pode retomar o controle ou trocar o
            playbook.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {canForceTakeover && (
              <button
                className="btn-ghost-mini btn-ghost-mini--primary"
                type="button"
                onClick={onOpenForceTakeover}
              >
                Retomar para o atendente (encerra a condução da IA)
              </button>
            )}
            {canForcePlaybook && (
              <button className="btn-ghost-mini" type="button" onClick={onOpenForcePlaybook}>
                Trocar playbook
              </button>
            )}
          </div>
        </section>
      )}

      {/* Outras conversas em paralelo (D10 — real) */}
      <section className="coatt-tools__section">
        <h3 className="coatt-tools__title">Outras conversas de {attendantName}</h3>
        {parallelConvs.length === 0 ? (
          <p style={{ color: 'var(--fg-3)', fontSize: 'var(--text-xs)', margin: 0 }}>
            Nenhuma outra conversa em andamento.
          </p>
        ) : (
          parallelConvs.map((p) => (
            <div key={p.id} className="coatt-parallel">
              <span>{p.customerName}</span>
              <span style={{ marginLeft: 'auto', color: 'var(--fg-3)' }}>
                {p.channelLabel} · {p.statusLabel}
              </span>
              <button
                className="btn-ghost-mini"
                type="button"
                onClick={() => {
                  onSwitchConversation(p.id);
                }}
              >
                Trocar
              </button>
            </div>
          ))
        )}
      </section>
    </aside>
  );
}

/** Tom da sentença → classe semafórica `.sla--*` já portada no CSS. */
function qualityToneToSla(
  tone: PanelQualityVM['sentimentTone'],
): 'green' | 'amber' | 'red' | 'neutral' {
  switch (tone) {
    case 'success':
      return 'green';
    case 'warning':
      return 'amber';
    case 'danger':
      return 'red';
    default:
      return 'neutral';
  }
}
